import { MapPinIcon } from '@heroicons/react/20/solid';
import Moment from 'moment/moment';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import type { AsyncStatus } from '../../../@types/AsyncStatus';
import type { CoproTableData } from '../../../@types/CoproTableData';
import type { RootState } from '../../../store/store';
import { getAttribution, getCopro } from '../../../utils/api';
import { coproTypes } from '../../../utils/copros';
import { JWTHeader } from '../../../utils/JWTHeader';
import Badge from '../../common/Badge';
import Map from '../../common/CoproMap';
import MUITable from '../../common/MUITable';
import { showToast } from '../../common/Notification';
import RedirectionCoproButtons from '../../common/RedirectionCoproButtons';
import Spinner from '../../common/Spinner';

const headerKeys = {
  remoteId: 'N° IMMAT',
  name: 'NOM',
  nonMatched: 'ADRESSE',
  nonMatched3: 'GESTIONNAIRE',
  batchSize: 'NB LOTS HAB / LOCAL PRO',
  type: 'TYPE',
  termEndAt: 'FIN DE MANDAT',
};

const headers = [
  { title: 'N° IMMAT', class: '' },
  { title: 'NOM', class: '' },
  { title: 'ADRESSE', class: '' },
  { title: 'GESTIONNAIRE', class: '' },
  { title: 'NB LOTS HAB / LOCAL PRO', class: '' },
  { title: 'TYPE', class: '' },
  { title: 'FIN DE MANDAT', class: '' },
  { title: 'MANDANT', class: '' },
];

const complementaryColors = [
  '#c70039', // Crimson
  '#900c3f', // Dark Magenta
  '#581845', // Eggplant
  '#ff5733', // Orange Red
  '#c70039', // Dark Red
  '#900c3f', // Deep Magenta
  '#581845', // Dark Purple
  '#8e44ad', // Amethyst
  '#3498db', // Sky Blue
  '#1abc9c', // Turquoise
  '#16a085', // Teal
  '#f39c12', // Sunflower Yellow
  '#d35400', // Pumpkin
  '#7f8c8d', // Concrete Gray
  '#34495e', // Wet Asphalt
  '#000', // Black
  '#fff', // White
  '#ff00ff', // Pink
];

const GESTIONNAIRE_FONCTION_TYPE = 1;

function CoproManaging() {
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);

  const [copros, setCopros] = useState(null);
  const [status, setStatus] = useState<AsyncStatus>('IDLE');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [filteredCoproData, setFilteredCoproData] = useState([]);
  const [coproColor, setCoproColor] = useState<{ [key: number]: string }>({});
  const [selectedLocationId, setSelectedLocationId] = useState<number>(null);

  function parseDate(dateStr: string): Date {
    const [day, month, year] = dateStr.split('/').map(Number);
    return new Date(year, month - 1, day); // month is 0-based in JavaScript Date
  }

  function sortCoprosByTermEndAt(coprosData: Partial<CoproTableData>[]): Partial<CoproTableData>[] {
    const nonNullKeys = coprosData.filter((key) => key['FIN DE MANDAT'] && key['FIN DE MANDAT']!.trim() !== '');
    const nullKeys = coprosData.filter((key) => !key['FIN DE MANDAT'] || key['FIN DE MANDAT']!.trim() === '');

    nonNullKeys.sort((a, b) => parseDate(b['FIN DE MANDAT']!).getTime() - parseDate(a['FIN DE MANDAT']!).getTime());

    return [...nonNullKeys, ...nullKeys];
  }

  async function fetchAttributions() {
    setStatus('PENDING');
    try {
      const response = await fetch(getAttribution(company.id), JWTHeader(jwt));

      if (!response.ok) {
        throw new Error('Error fetching attribution');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching attributions :', error);
      setStatus('ERROR');
      setErrorMessage('Erreur lors du chargement des attributions');
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
    }
  }

  async function fetchCopros() {
    setStatus('PENDING');
    try {
      const response = await fetch(getCopro(company.id, true, null, 'termEndAt', 'desc'), JWTHeader(jwt));
      const result = await response.json();

      // get the attribution data
      const attributionData = await fetchAttributions();

      const colorMapping = {};
      let colorIndex = 0;
      const dataCoproColor = {};

      const registeredCopro = result.results = sortCoprosByTermEndAt(result.results.map((copro) => {
        const newItem: Partial<CoproTableData> = {};

        Object.keys(copro).forEach((key) => {
          if (key === 'termEndAt' && copro[key]) {
            newItem[headerKeys[key]] = Moment(copro[key]).format('DD/MM/YYYY');
          } else if (Object.keys(headerKeys).includes(key)) {
            newItem[headerKeys[key]] = copro[key];
          }
        });

        newItem.TYPE = coproTypes[copro.type];

        newItem.id = copro.id;

        newItem.NOM = (
          <div className="flex items-center">
            <RedirectionCoproButtons coproId={copro.id} />
            {copro.name}
          </div>
        );

        // add the gestionnaire first and lastname
        const attribution = attributionData.find((iAttribution) => iAttribution.copro.id == copro.id && iAttribution.fonctionType == GESTIONNAIRE_FONCTION_TYPE);
        if (attribution) {
          newItem.GESTIONNAIRE = `${attribution.user.firstname} ${attribution.user.lastname ? attribution.user.lastname.toUpperCase() : ''}`;

          // Assign a color to the gestionnaire
          if (!colorMapping[attribution.user.id]) {
            colorMapping[attribution.user.id] = complementaryColors[colorIndex % complementaryColors.length];
            colorIndex++;
          }

          dataCoproColor[copro.id] = colorMapping[attribution.user.id];
        } else {
          newItem.GESTIONNAIRE = '';
        }

        newItem.MANDANT = copro.company.id !== company.id && copro.company.subcontractors.some((subcontractor) => subcontractor.id === company.id)
          ? (
            <div className="text-xs">
              {copro.company.name}
            </div>
          ) : '';

        if (copro.addresses.length === 0) {
          newItem.ADRESSE = '';
          return newItem;
        }
        newItem.ADRESSE = (
          <div className="flex items-center">
            <div className="flex h-8 w-8 cursor-pointer items-center justify-center">
              <MapPinIcon
                className="mr-2 h-5 w-5 text-gray-600 hover:text-gray-800"
                onClick={() => {
                  const mapCopros = document.getElementById('map-copros');
                  const mainScrollable = document.getElementById('mainScrollable');
                  if (mapCopros && mainScrollable) {
                    const mapCoprosRect = mapCopros.getBoundingClientRect();
                    const scrollTop = mainScrollable.scrollTop + mapCoprosRect.top - 85;
                    mainScrollable.scrollTo({
                      top: scrollTop,
                      behavior: 'smooth',
                    });
                  }
                  setSelectedLocationId(copro.id);
                }}
              />
            </div>
            {`${copro.addresses[0].address} ${copro.addresses[0].postalCode} ${copro.addresses[0].city}`}
          </div>
        );
        return newItem;
      }));

      setCopros(registeredCopro);
      setCoproColor(dataCoproColor);
      setStatus('SUCCESS');
    } catch (error) {
      console.error('Error fetching copros :', error);
      setStatus('ERROR');
      setErrorMessage('Erreur lors du chargement des copropriétés');
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
    }
  }

  useEffect(() => {
    if (company && company.id && jwt) {
      fetchCopros();
    }
  }, [company, jwt]);

  return (
    <div className="flex flex-col">
      <div className="mb-8 overflow-x-hidden rounded-lg bg-white p-4 shadow-md">
        <div className="mt-4 mb-12 text-lg">
          <p>
            Cette page vous offre une vue d'ensemble sur la répartition de vos gestionnaires.
            <br />
            Chacun de vos gestionnaires est associé à une couleur unique pour une identification rapide. Vous pouvez voir les copropriétés associées à chaque gestionnaire en cliquant sur leur nom dans le tableau en dessous.
          </p>
        </div>
        {status === 'SUCCESS' && (
          <div className="my-10">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-1">
              <div className="flex h-[55dvh] min-h-80 flex-col">
                <Map coproIds={filteredCoproData.map((data) => data.id)} coproColor={coproColor} selectedLocationIdProp={selectedLocationId} />
              </div>
              <div className="flex flex-col justify-start">
                <MUITable
                  data={copros}
                  headers={headers}
                  tableClass="min-w-full bg-white border text-sm"
                  filterColumnName="GESTIONNAIRE"
                  setParentFilteredData={setFilteredCoproData}
                  searchClassName="w-full"
                  sortableColumns={['N° IMMAT', 'NOM', 'ADRESSE', 'GESTIONNAIRE', 'NB LOTS HAB / LOCAL PRO', 'TYPE', 'FIN DE MANDAT', 'MANDANT']}
                />
                {filteredCoproData.length === 0 && (
                  <div className="mx-10 my-10 text-center">
                    <Badge mode="warning" text="Aucune copropriété trouvée" />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {status === 'ERROR' && (
          <div className="mx-10 my-32 text-center">
            <Badge mode="error" text={errorMessage} />
          </div>
        )}
        {status === 'PENDING' && (
          <p className="mx-10 my-32 text-center">
            <Spinner aria-label="Center-aligned spinner" size="xl" />
          </p>
        )}
      </div>
    </div>
  );
}

export default CoproManaging;
